/**
 * @fileoverview WGSL compute-шейдеры v10.0.0
 * @module MikhmelViewer/Cinematic10/WGSLCompute
 *
 * Проходы, которым нужен произвольный доступ к памяти или редукция —
 * то, ради чего вообще имеет смысл переход на WebGPU:
 *
 *  • denoise      — edge-aware денойзинг с направленной анизотропией
 *  • fsrEasu      — FSR 1.0, апскейл с адаптацией к направлению границ
 *  • fsrRcas      — FSR 1.0, контрастно-адаптивная резкость
 *  • sceneStats   — редукция кадра в вектор признаков для AI-пресетов
 *  • nerfRender   — объёмный ray marching через hash-grid + MLP
 *  • volumeRaymarch — прямой рендеринг медицинского 3D-объёма
 *  • bilateralUpsample — подъём half-res эффектов до полного разрешения
 *
 * ВНИМАНИЕ по сигнатуре textureStore. Для texture_storage_2d правильная форма —
 * три аргумента: textureStore(tex, coords, value). Вариант с четырьмя
 * аргументами (tex, coords, arrayIndex, value) относится только к
 * texture_storage_2d_array и на обычной 2D-текстуре не скомпилируется.
 */

// ═══════════════════════════════════════════════════════════════════
// 1. ОБЩИЙ ПРОЛОГ
// ═══════════════════════════════════════════════════════════════════

export const COMPUTE_COMMON = /* wgsl */`
const PI: f32 = 3.14159265359;
const TAU: f32 = 6.28318530718;
const LUMA709: vec3f = vec3f(0.2126, 0.7152, 0.0722);
const LUMA601: vec3f = vec3f(0.299, 0.587, 0.114);

fn luma(c: vec3f) -> f32 { return dot(c, LUMA709); }
fn luma601(c: vec3f) -> f32 { return dot(c, LUMA601); }

fn safeDiv(a: f32, b: f32) -> f32 { return a / max(b, 1e-6); }

fn hash21(p: vec2f) -> f32 {
  return fract(sin(dot(p, vec2f(127.1, 311.7))) * 43758.5453);
}

// Загрузка с зажатием координат по границе текстуры
fn loadClamped(t: texture_2d<f32>, coord: vec2i, dims: vec2i) -> vec4f {
  let c = clamp(coord, vec2i(0), dims - vec2i(1));
  return textureLoad(t, c, 0);
}
`;

// ═══════════════════════════════════════════════════════════════════
// 2. DENOISE — направленный edge-aware денойзинг
// ═══════════════════════════════════════════════════════════════════

export const COMP_DENOISE = /* wgsl */`
struct Params {
  uResolution: vec2u,
  uDepthSigma: f32,
  uColorSigma: f32,
  uSpatialSigma: f32,
  uRadius: i32,
  uHasDepth: i32,
  uAnisotropic: i32,
  uStrength: f32,
  uFrame: f32,
};

@group(0) @binding(0) var<uniform> U: Params;
@group(0) @binding(1) var texSrc: texture_2d<f32>;
@group(0) @binding(2) var texDepth: texture_2d<f32>;
@group(0) @binding(3) var texDst: texture_storage_2d<rgba16float, write>;

${COMPUTE_COMMON}

var<workgroup> tile: array<vec4f, 144>; // (8 + 2*2) * (8 + 2*2) = 144

fn depthAt(coord: vec2i, dims: vec2i) -> f32 {
  if (U.uHasDepth != 0) {
    return loadClamped(texDepth, coord, dims).r;
  }
  return luma601(loadClamped(texSrc, coord, dims).rgb);
}

@compute @workgroup_size(8, 8, 1)
fn cs_main(
  @builtin(global_invocation_id) gid: vec3u,
  @builtin(local_invocation_id) lid: vec3u
) {
  let dims = vec2i(textureDimensions(texSrc));
  let coord = vec2i(gid.xy);

  // [10.19.13] ЗДЕСЬ БЫЛА ОШИБКА, ИЗ-ЗА КОТОРОЙ НЕ СОБИРАЛСЯ НИ ОДИН КАДР.
  //
  // Стояло: "if (coord.x >= dims.x || coord.y >= dims.y) { return; }", а ниже —
  // workgroupBarrier(). WGSL это запрещает: барьера обязаны достичь ВСЕ потоки
  // группы (однородный поток управления). Часть потоков уходила по return, и
  // Dawn (Chrome/Electron, Windows) отвергал модуль целиком:
  //
  //   error: 'workgroupBarrier' must only be called from uniform control flow
  //   note: control flow depends on possibly non-uniform value
  //
  // Последствие не косметическое: невалидный модуль → невалидный
  // ComputePipeline "compute:denoise" → невалидный CommandBuffer КАЖДОГО кадра →
  // Queue.Submit отвергается. В журнале это лавина «[Invalid CommandBuffer ...
  // frame:N]» при живом счётчике кадров. В песочнице ошибку увидеть нельзя
  // вообще: там адаптер WebGPU отказывает и работает откат на WebGL2, где этого
  // шейдера нет. Нашлось по журналу с машины с работающим WebGPU (RTX 4060).
  //
  // Правильно: из шейдера не выходим. Границу проверяем один раз и держим в
  // переменной. Загрузка тайла безопасна и за краем (loadClamped зажимает
  // координаты), барьер зовут все потоки, а лишнее отсекается при ЗАПИСИ.
  let внеКадра = coord.x >= dims.x || coord.y >= dims.y;

  // ── Загрузка тайла 12×12 в разделяемую память ──
  // Каждый из 64 потоков группы тянет 3 текселя (64*3 = 192 ≥ 144),
  // после чего окно 5×5 читается из workgroup-памяти, а не из VRAM
  let tileOrigin = vec2i(gid.xy) - vec2i(lid.xy) - vec2i(2);
  let localIdx = i32(lid.y * 8u + lid.x);
  for (var k = 0; k < 3; k++) {
    let idx = localIdx * 3 + k;
    if (idx < 144) {
      let tx = idx % 12;
      let ty = idx / 12;
      tile[idx] = loadClamped(texSrc, tileOrigin + vec2i(tx, ty), dims);
    }
  }
  workgroupBarrier();

  let centerIdx = i32(lid.y + 2u) * 12 + i32(lid.x + 2u);
  let center = tile[centerIdx];
  let centerLuma = luma(center.rgb);
  let centerDepth = depthAt(coord, dims);

  // ── Определение направления границы по структурному тензору ──
  // Фильтруем вдоль границы, а не поперёк — иначе денойзер съедает детали
  var gx = vec3f(0.0);
  var gy = vec3f(0.0);
  for (var i = -1; i <= 1; i++) {
    for (var j = -1; j <= 1; j++) {
      let s = tile[(i32(lid.y) + 2 + j) * 12 + (i32(lid.x) + 2 + i)].rgb;
      let sobelX = f32(i) * select(1.0, 2.0, j == 0);
      let sobelY = f32(j) * select(1.0, 2.0, i == 0);
      gx += s * sobelX;
      gy += s * sobelY;
    }
  }
  let gradX = luma(gx);
  let gradY = luma(gy);
  let gradMag = sqrt(gradX * gradX + gradY * gradY);

  // Единичный вектор ВДОЛЬ границы (перпендикуляр к градиенту)
  var edgeDir = vec2f(1.0, 0.0);
  if (gradMag > 1e-4) { edgeDir = normalize(vec2f(-gradY, gradX)); }
  let anisotropy = select(0.0, clamp(gradMag * 4.0, 0.0, 1.0), U.uAnisotropic != 0);

  // ── Билатеральная фильтрация с анизотропным ядром ──
  let r = clamp(U.uRadius, 1, 2);
  let spatialFalloff = 1.0 / max(2.0 * U.uSpatialSigma * U.uSpatialSigma, 1e-4);

  var sum = vec3f(0.0);
  var weightSum = 0.0;

  for (var dy = -2; dy <= 2; dy++) {
    if (dy < -r || dy > r) { continue; }
    for (var dx = -2; dx <= 2; dx++) {
      if (dx < -r || dx > r) { continue; }

      let off = vec2f(f32(dx), f32(dy));
      let s = tile[(i32(lid.y) + 2 + dy) * 12 + (i32(lid.x) + 2 + dx)];
      let sDepth = depthAt(coord + vec2i(dx, dy), dims);

      // Растягиваем ядро вдоль границы, сжимаем поперёк
      var effOff = off;
      if (anisotropy > 0.001) {
        let along = dot(off, edgeDir);
        let across = dot(off, vec2f(-edgeDir.y, edgeDir.x));
        effOff = vec2f(along * (1.0 - anisotropy * 0.6), across * (1.0 + anisotropy * 1.5));
      }

      let wSpatial = exp(-dot(effOff, effOff) * spatialFalloff);
      let wDepth = exp(-abs(sDepth - centerDepth) * U.uDepthSigma);
      let wColor = exp(-abs(luma(s.rgb) - centerLuma) * U.uColorSigma);
      let w = wSpatial * wDepth * wColor;

      sum += s.rgb * w;
      weightSum += w;
    }
  }

  let filtered = select(center.rgb, sum / weightSum, weightSum > 1e-5);
  let result = mix(center.rgb, filtered, U.uStrength);
  // [10.19.13] Отсечение за краем кадра — здесь, а не выходом из шейдера:
  // запись условная, барьер выше — безусловный.
  if (!внеКадра) {
    textureStore(texDst, coord, vec4f(result, center.a));
  }
}
`;

// ═══════════════════════════════════════════════════════════════════
// 3. FSR 1.0 — EASU (Edge Adaptive Spatial Upsampling)
// ═══════════════════════════════════════════════════════════════════

export const COMP_FSR_EASU = /* wgsl */`
struct Params {
  uInputSize: vec2f,
  uOutputSize: vec2f,
  uSharpness: f32,
  uPad0: f32,
};

@group(0) @binding(0) var<uniform> U: Params;
@group(0) @binding(1) var texSrc: texture_2d<f32>;
@group(0) @binding(2) var texDst: texture_storage_2d<rgba16float, write>;

${COMPUTE_COMMON}

// Ядро EASU: направленно-адаптивное окно. Вес считается по расстоянию в
// пространстве, растянутом вдоль обнаруженной границы, поэтому апскейл не
// «лесенит» диагонали, как обычный бикубик.
fn easuTap(
  accum: ptr<function, vec3f>,
  weightAcc: ptr<function, f32>,
  offset: vec2f,
  dir: vec2f,
  len: f32,
  lob: f32,
  clp: f32,
  color: vec3f
) {
  // Поворот смещения в базис границы
  var v = vec2f(dot(offset, dir), dot(offset, vec2f(-dir.y, dir.x)));
  v *= vec2f(1.0, 1.0 + len * 0.5);

  var d2 = dot(v, v) * clp;
  d2 = min(d2, 1.0);

  // Двухлепестковое окно: (a*d²+b)² * (c*d²+e)²  — приближение Ланцоша
  let wA = lob * d2 - 1.0;
  let wB = (2.0 / 5.0) * d2 - 1.0;
  let w = (wA * wA) * (wB * wB - 1.0) * (25.0 / 16.0) - (25.0 / 16.0 - 1.0);

  *accum += color * w;
  *weightAcc += w;
}

@compute @workgroup_size(8, 8, 1)
fn cs_main(@builtin(global_invocation_id) gid: vec3u) {
  let outDims = vec2i(U.uOutputSize);
  let coord = vec2i(gid.xy);
  if (coord.x >= outDims.x || coord.y >= outDims.y) { return; }

  let srcDims = vec2i(U.uInputSize);
  let scale = U.uInputSize / U.uOutputSize;

  // Позиция выходного пикселя в координатах источника
  let srcPos = (vec2f(coord) + vec2f(0.5)) * scale - vec2f(0.5);
  let base = vec2i(floor(srcPos));
  let frac = srcPos - floor(srcPos);

  // Окрестность 4×4
  var c: array<vec3f, 16>;
  for (var y = 0; y < 4; y++) {
    for (var x = 0; x < 4; x++) {
      c[y * 4 + x] = loadClamped(texSrc, base + vec2i(x - 1, y - 1), srcDims).rgb;
    }
  }

  // ── Анализ направления границы по центральному кресту ──
  let lC = luma(c[5]);
  let lN = luma(c[1]);
  let lS = luma(c[9]);
  let lW = luma(c[4]);
  let lE = luma(c[6]);

  // Горизонтальная и вертикальная «сила» границы
  var dirX = lW - lE;
  var dirY = lN - lS;
  let lenX = max(abs(lW - lC), abs(lE - lC));
  let lenY = max(abs(lN - lC), abs(lS - lC));

  var dir = vec2f(dirX, dirY);
  let dirLen = length(dir);
  if (dirLen > 1e-4) { dir = dir / dirLen; } else { dir = vec2f(1.0, 0.0); }

  // len ∈ [0,1] — насколько выражена граница; на плоских участках → 0,
  // и фильтр вырождается в изотропный, что и нужно
  let edgeLen = clamp(max(lenX, lenY) * 4.0, 0.0, 1.0);

  // Резкость управляет шириной лепестка
  let lob = mix(-0.40, -0.20, clamp(U.uSharpness, 0.0, 1.0));
  let clp = 1.0 / mix(2.2, 1.6, clamp(U.uSharpness, 0.0, 1.0));

  var accum = vec3f(0.0);
  var weightAcc = 0.0;

  for (var y = 0; y < 4; y++) {
    for (var x = 0; x < 4; x++) {
      let offset = vec2f(f32(x - 1), f32(y - 1)) - frac;
      easuTap(&accum, &weightAcc, offset, dir, edgeLen, lob, clp, c[y * 4 + x]);
    }
  }

  var result = c[5];
  if (abs(weightAcc) > 1e-5) { result = accum / weightAcc; }

  // Зажим в диапазон непосредственных соседей — страховка от «звона»
  // (ringing), который двухлепестковое ядро даёт на резких перепадах
  let minC = min(min(c[5], c[6]), min(c[9], c[10]));
  let maxC = max(max(c[5], c[6]), max(c[9], c[10]));
  result = clamp(result, minC, maxC);

  textureStore(texDst, coord, vec4f(max(result, vec3f(0.0)), 1.0));
}
`;

// ═══════════════════════════════════════════════════════════════════
// 4. FSR 1.0 — RCAS (Robust Contrast Adaptive Sharpening)
// ═══════════════════════════════════════════════════════════════════

export const COMP_FSR_RCAS = /* wgsl */`
struct Params {
  uResolution: vec2u,
  uSharpness: f32,
  uDenoise: i32,
};

@group(0) @binding(0) var<uniform> U: Params;
@group(0) @binding(1) var texSrc: texture_2d<f32>;
@group(0) @binding(2) var texDst: texture_storage_2d<rgba16float, write>;

${COMPUTE_COMMON}

@compute @workgroup_size(8, 8, 1)
fn cs_main(@builtin(global_invocation_id) gid: vec3u) {
  let dims = vec2i(textureDimensions(texSrc));
  let coord = vec2i(gid.xy);
  if (coord.x >= dims.x || coord.y >= dims.y) { return; }

  // Крестообразная окрестность
  let e = loadClamped(texSrc, coord, dims).rgb;
  let b = loadClamped(texSrc, coord + vec2i( 0, -1), dims).rgb;
  let d = loadClamped(texSrc, coord + vec2i(-1,  0), dims).rgb;
  let f = loadClamped(texSrc, coord + vec2i( 1,  0), dims).rgb;
  let h = loadClamped(texSrc, coord + vec2i( 0,  1), dims).rgb;

  // Локальный диапазон по каждому каналу
  let mn4 = min(min(b, d), min(f, h));
  let mx4 = max(max(b, d), max(f, h));

  // Ключ резкости: чем больше локальный контраст, тем слабее подъём —
  // отсюда «robust» в названии, обычный CAS на контрастных краях звенит
  let sharp = exp2(-mix(0.0, 3.0, clamp(U.uSharpness, 0.0, 1.0)));
  let hitMin = mn4 / (4.0 * mx4 + vec3f(1e-5));
  let hitMax = (vec3f(1.0) - mx4) / (4.0 * mx4 - 4.0 * mn4 + vec3f(1e-5));
  let lobeRGB = max(-hitMin, hitMax);
  var lobe = max(-0.1875, min(max(lobeRGB.r, max(lobeRGB.g, lobeRGB.b)), 0.0)) * sharp;

  // Опциональное подавление шума: гасим резкость там, где локальный
  // разброс мал — то есть усиливать нечего, кроме шума сенсора
  if (U.uDenoise != 0) {
    let nz = luma(mx4 - mn4);
    let noiseGate = 1.0 - clamp(1.0 - nz * 8.0, 0.0, 1.0);
    lobe *= noiseGate;
  }

  let rcpL = 1.0 / (1.0 + 4.0 * lobe);
  let result = (lobe * (b + d + f + h) + e) * rcpL;

  textureStore(texDst, coord, vec4f(clamp(result, vec3f(0.0), vec3f(65504.0)), 1.0));
}
`;

// ═══════════════════════════════════════════════════════════════════
// 5. SCENE STATS — редукция кадра в вектор признаков
// ═══════════════════════════════════════════════════════════════════

export const COMP_SCENE_STATS = /* wgsl */`
struct Params {
  uResolution: vec2u,
  uSampleStride: i32,
  uHasDepth: i32,
};

// Атомарные операции в WGSL работают только с u32/i32, поэтому величины
// накапливаются в фиксированной точке (×1e6) и делятся уже на CPU.
struct Stats {
  lumaSum: atomic<u32>,
  lumaSqSum: atomic<u32>,
  gradSum: atomic<u32>,
  minLuma: atomic<u32>,
  maxLuma: atomic<u32>,
  darkPixels: atomic<u32>,
  brightPixels: atomic<u32>,
  edgePixels: atomic<u32>,
  depthSum: atomic<u32>,
  sampleCount: atomic<u32>,
  histogram: array<atomic<u32>, 16>,
};

@group(0) @binding(0) var<uniform> U: Params;
@group(0) @binding(1) var texSrc: texture_2d<f32>;
@group(0) @binding(2) var texDepth: texture_2d<f32>;
@group(0) @binding(3) var<storage, read_write> stats: Stats;

${COMPUTE_COMMON}

const FIXED: f32 = 1000000.0;

@compute @workgroup_size(8, 8, 1)
fn cs_main(@builtin(global_invocation_id) gid: vec3u) {
  let dims = vec2i(textureDimensions(texSrc));
  let stride = max(U.uSampleStride, 1);
  let coord = vec2i(gid.xy) * stride;

  if (coord.x >= dims.x || coord.y >= dims.y) { return; }

  let c = loadClamped(texSrc, coord, dims).rgb;
  let l = clamp(luma(c), 0.0, 1.0);

  // Градиент — прокси сложности сцены (много деталей → выше)
  let cx = loadClamped(texSrc, coord + vec2i(1, 0), dims).rgb;
  let cy = loadClamped(texSrc, coord + vec2i(0, 1), dims).rgb;
  let grad = abs(luma(cx) - l) + abs(luma(cy) - l);

  atomicAdd(&stats.lumaSum, u32(l * FIXED));
  atomicAdd(&stats.lumaSqSum, u32(l * l * FIXED));
  atomicAdd(&stats.gradSum, u32(clamp(grad, 0.0, 1.0) * FIXED));
  atomicAdd(&stats.sampleCount, 1u);

  atomicMin(&stats.minLuma, u32(l * FIXED));
  atomicMax(&stats.maxLuma, u32(l * FIXED));

  if (l < 0.08) { atomicAdd(&stats.darkPixels, 1u); }
  if (l > 0.92) { atomicAdd(&stats.brightPixels, 1u); }
  if (grad > 0.08) { atomicAdd(&stats.edgePixels, 1u); }

  if (U.uHasDepth != 0) {
    let d = clamp(loadClamped(texDepth, coord, dims).r, 0.0, 1.0);
    atomicAdd(&stats.depthSum, u32(d * FIXED));
  }

  // Гистограмма яркости на 16 корзин — по ней AI-оптимизатор понимает,
  // недоэкспонирован кадр, переэкспонирован или имеет провал в середине
  let bin = u32(clamp(l * 15.999, 0.0, 15.0));
  atomicAdd(&stats.histogram[bin], 1u);
}
`;

// ═══════════════════════════════════════════════════════════════════
// 6. BILATERAL UPSAMPLE — подъём half-res эффектов до полного разрешения
// ═══════════════════════════════════════════════════════════════════

export const COMP_BILATERAL_UPSAMPLE = /* wgsl */`
struct Params {
  uLowResSize: vec2f,
  uFullResSize: vec2f,
  uDepthSigma: f32,
  uHasDepth: i32,
};

@group(0) @binding(0) var<uniform> U: Params;
@group(0) @binding(1) var texLow: texture_2d<f32>;
@group(0) @binding(2) var texDepthLow: texture_2d<f32>;
@group(0) @binding(3) var texDepthFull: texture_2d<f32>;
@group(0) @binding(4) var texDst: texture_storage_2d<rgba16float, write>;

${COMPUTE_COMMON}

@compute @workgroup_size(8, 8, 1)
fn cs_main(@builtin(global_invocation_id) gid: vec3u) {
  let fullDims = vec2i(U.uFullResSize);
  let coord = vec2i(gid.xy);
  if (coord.x >= fullDims.x || coord.y >= fullDims.y) { return; }

  let lowDims = vec2i(U.uLowResSize);
  let ratio = U.uLowResSize / U.uFullResSize;
  let lowPos = (vec2f(coord) + vec2f(0.5)) * ratio - vec2f(0.5);
  let base = vec2i(floor(lowPos));
  let frac = lowPos - floor(lowPos);

  if (U.uHasDepth == 0) {
    // Без глубины — обычная билинейная интерполяция
    let c00 = loadClamped(texLow, base + vec2i(0, 0), lowDims);
    let c10 = loadClamped(texLow, base + vec2i(1, 0), lowDims);
    let c01 = loadClamped(texLow, base + vec2i(0, 1), lowDims);
    let c11 = loadClamped(texLow, base + vec2i(1, 1), lowDims);
    let top = mix(c00, c10, frac.x);
    let bot = mix(c01, c11, frac.x);
    textureStore(texDst, coord, mix(top, bot, frac.y));
    return;
  }

  // Билинейные веса, домноженные на близость по глубине. Без этого
  // half-res эффект «протекает» через силуэты объектов на фон.
  let targetDepth = loadClamped(texDepthFull, coord, fullDims).r;
  let bilinear = array<f32, 4>(
    (1.0 - frac.x) * (1.0 - frac.y),
    frac.x * (1.0 - frac.y),
    (1.0 - frac.x) * frac.y,
    frac.x * frac.y
  );
  let offsets = array<vec2i, 4>(vec2i(0, 0), vec2i(1, 0), vec2i(0, 1), vec2i(1, 1));

  var accum = vec4f(0.0);
  var weightSum = 0.0;

  for (var i = 0; i < 4; i++) {
    let sampleCoord = base + offsets[i];
    let sampleDepth = loadClamped(texDepthLow, sampleCoord, lowDims).r;
    let wDepth = exp(-abs(sampleDepth - targetDepth) * U.uDepthSigma);
    let w = bilinear[i] * wDepth + 1e-6;

    accum += loadClamped(texLow, sampleCoord, lowDims) * w;
    weightSum += w;
  }

  textureStore(texDst, coord, accum / weightSum);
}
`;

// ═══════════════════════════════════════════════════════════════════
// 7. NERF — inference через multi-resolution hash grid + tiny MLP
// ═══════════════════════════════════════════════════════════════════

export const COMP_NERF_RENDER = /* wgsl */`
struct Params {
  uInvViewProj: mat4x4f,
  uCameraPos: vec4f,
  uBBoxMin: vec4f,
  uBBoxMax: vec4f,
  uResolution: vec2u,
  uSteps: i32,
  uLevels: i32,          // число уровней hash-grid
  uFeaturesPerLevel: i32,
  uHashTableSize: u32,
  uBaseResolution: f32,
  uPerLevelScale: f32,
  uDensityScale: f32,
  uStepJitter: f32,
  uHiddenDim: i32,
  uFrame: f32,
};

@group(0) @binding(0) var<uniform> U: Params;
@group(0) @binding(1) var<storage, read> hashTable: array<f32>;
@group(0) @binding(2) var<storage, read> mlpWeights: array<f32>;
@group(0) @binding(3) var texDst: texture_storage_2d<rgba16float, write>;

${COMPUTE_COMMON}

const MAX_LEVELS: i32 = 16;
const MAX_HIDDEN: i32 = 64;

// Простые числа из Instant-NGP: пространственный хеш с малой коллизионностью
const HASH_P1: u32 = 1u;
const HASH_P2: u32 = 2654435761u;
const HASH_P3: u32 = 805459861u;

fn spatialHash(pos: vec3i, tableSize: u32) -> u32 {
  let h = (u32(pos.x) * HASH_P1) ^ (u32(pos.y) * HASH_P2) ^ (u32(pos.z) * HASH_P3);
  return h % tableSize;
}

// Трилинейная интерполяция признаков одного уровня решётки
fn gridFeature(level: i32, p: vec3f, out: ptr<function, array<f32, 8>>) {
  let scale = U.uBaseResolution * pow(U.uPerLevelScale, f32(level));
  let scaled = p * scale;
  let base = vec3i(floor(scaled));
  let frac = scaled - floor(scaled);

  let fpl = U.uFeaturesPerLevel;
  let levelOffset = u32(level) * U.uHashTableSize * u32(fpl);

  for (var f = 0; f < 8; f++) { (*out)[f] = 0.0; }

  for (var corner = 0; corner < 8; corner++) {
    let cx = corner & 1;
    let cy = (corner >> 1) & 1;
    let cz = (corner >> 2) & 1;

    let cornerPos = base + vec3i(cx, cy, cz);
    let w = (select(1.0 - frac.x, frac.x, cx == 1))
          * (select(1.0 - frac.y, frac.y, cy == 1))
          * (select(1.0 - frac.z, frac.z, cz == 1));

    let idx = spatialHash(cornerPos, U.uHashTableSize);
    let readBase = levelOffset + idx * u32(fpl);

    for (var f = 0; f < 8; f++) {
      if (f >= fpl) { break; }
      (*out)[f] += hashTable[readBase + u32(f)] * w;
    }
  }
}

// Tiny MLP: encoding → hidden (ReLU) → hidden (ReLU) → [density, rgb]
// Веса лежат подряд: W1, b1, W2, b2, W3, b3
fn evalMLP(encoding: ptr<function, array<f32, 128>>, encDim: i32, viewDir: vec3f) -> vec4f {
  let hidden = clamp(U.uHiddenDim, 16, MAX_HIDDEN);

  var layer1: array<f32, MAX_HIDDEN>;
  var offset = 0u;

  // Слой 1: encDim → hidden
  for (var o = 0; o < MAX_HIDDEN; o++) {
    if (o >= hidden) { break; }
    var acc = 0.0;
    for (var i = 0; i < 128; i++) {
      if (i >= encDim) { break; }
      acc += (*encoding)[i] * mlpWeights[offset + u32(o * encDim + i)];
    }
    layer1[o] = acc;
  }
  offset += u32(hidden * encDim);
  for (var o = 0; o < MAX_HIDDEN; o++) {
    if (o >= hidden) { break; }
    layer1[o] = max(layer1[o] + mlpWeights[offset + u32(o)], 0.0); // ReLU
  }
  offset += u32(hidden);

  // Слой 2: hidden(+3 направления вида) → hidden
  var layer2: array<f32, MAX_HIDDEN>;
  let in2 = hidden + 3;
  for (var o = 0; o < MAX_HIDDEN; o++) {
    if (o >= hidden) { break; }
    var acc = 0.0;
    for (var i = 0; i < MAX_HIDDEN; i++) {
      if (i >= hidden) { break; }
      acc += layer1[i] * mlpWeights[offset + u32(o * in2 + i)];
    }
    // Направление взгляда подмешивается во второй слой — так модель
    // выучивает зависящие от ракурса блики, а плотность остаётся от них независимой
    acc += viewDir.x * mlpWeights[offset + u32(o * in2 + hidden)];
    acc += viewDir.y * mlpWeights[offset + u32(o * in2 + hidden + 1)];
    acc += viewDir.z * mlpWeights[offset + u32(o * in2 + hidden + 2)];
    layer2[o] = acc;
  }
  offset += u32(hidden * in2);
  for (var o = 0; o < MAX_HIDDEN; o++) {
    if (o >= hidden) { break; }
    layer2[o] = max(layer2[o] + mlpWeights[offset + u32(o)], 0.0);
  }
  offset += u32(hidden);

  // Выходной слой: hidden → 4 (плотность + RGB)
  var out4: array<f32, 4>;
  for (var o = 0; o < 4; o++) {
    var acc = 0.0;
    for (var i = 0; i < MAX_HIDDEN; i++) {
      if (i >= hidden) { break; }
      acc += layer2[i] * mlpWeights[offset + u32(o * hidden + i)];
    }
    out4[o] = acc + mlpWeights[offset + u32(4 * hidden) + u32(o)];
  }

  // Плотность через softplus (всегда ≥ 0), цвет через сигмоиду в [0,1]
  let density = log(1.0 + exp(clamp(out4[0], -20.0, 20.0)));
  let rgb = vec3f(
    1.0 / (1.0 + exp(-clamp(out4[1], -20.0, 20.0))),
    1.0 / (1.0 + exp(-clamp(out4[2], -20.0, 20.0))),
    1.0 / (1.0 + exp(-clamp(out4[3], -20.0, 20.0)))
  );
  return vec4f(rgb, density);
}

// Пересечение луча с axis-aligned боксом (метод slab)
fn intersectBox(origin: vec3f, dir: vec3f, bmin: vec3f, bmax: vec3f) -> vec2f {
  let invDir = 1.0 / dir;
  let t0 = (bmin - origin) * invDir;
  let t1 = (bmax - origin) * invDir;
  let tSmall = min(t0, t1);
  let tBig = max(t0, t1);
  let tNear = max(max(tSmall.x, tSmall.y), tSmall.z);
  let tFar = min(min(tBig.x, tBig.y), tBig.z);
  return vec2f(tNear, tFar);
}

@compute @workgroup_size(8, 8, 1)
fn cs_main(@builtin(global_invocation_id) gid: vec3u) {
  let dims = vec2i(U.uResolution);
  let coord = vec2i(gid.xy);
  if (coord.x >= dims.x || coord.y >= dims.y) { return; }

  let uv = (vec2f(coord) + vec2f(0.5)) / vec2f(dims);
  let ndc = vec4f(uv.x * 2.0 - 1.0, (1.0 - uv.y) * 2.0 - 1.0, 1.0, 1.0);
  let world = U.uInvViewProj * ndc;
  let farPoint = world.xyz / max(world.w, 1e-6);

  let origin = U.uCameraPos.xyz;
  let dir = normalize(farPoint - origin);

  let bmin = U.uBBoxMin.xyz;
  let bmax = U.uBBoxMax.xyz;
  let hit = intersectBox(origin, dir, bmin, bmax);

  if (hit.y <= max(hit.x, 0.0)) {
    textureStore(texDst, coord, vec4f(0.0));
    return;
  }

  let tStart = max(hit.x, 0.0);
  let tEnd = hit.y;
  let steps = clamp(U.uSteps, 8, 256);
  let dt = (tEnd - tStart) / f32(steps);

  // Дизеринг стартовой точки: без него объём выглядит слоёным
  let jitter = hash21(vec2f(coord) + vec2f(U.uFrame)) * U.uStepJitter;

  var accumColor = vec3f(0.0);
  var transmittance = 1.0;
  let levels = clamp(U.uLevels, 1, MAX_LEVELS);
  let encDim = levels * U.uFeaturesPerLevel;

  for (var s = 0; s < 256; s++) {
    if (s >= steps) { break; }
    if (transmittance < 0.005) { break; }

    let t = tStart + (f32(s) + jitter) * dt;
    let pos = origin + dir * t;

    // Нормализация в [0,1]³ — область определения hash-grid
    let normPos = (pos - bmin) / max(bmax - bmin, vec3f(1e-6));

    var encoding: array<f32, 128>;
    for (var i = 0; i < 128; i++) { encoding[i] = 0.0; }

    for (var level = 0; level < MAX_LEVELS; level++) {
      if (level >= levels) { break; }
      var feat: array<f32, 8>;
      gridFeature(level, normPos, &feat);
      let writeBase = level * U.uFeaturesPerLevel;
      for (var f = 0; f < 8; f++) {
        if (f >= U.uFeaturesPerLevel) { break; }
        if (writeBase + f < 128) { encoding[writeBase + f] = feat[f]; }
      }
    }

    let result = evalMLP(&encoding, encDim, dir);
    let density = result.a * U.uDensityScale;

    // Классическое интегрирование объёмного рендеринга
    let alpha = 1.0 - exp(-density * dt);
    accumColor += transmittance * alpha * result.rgb;
    transmittance *= (1.0 - alpha);
  }

  textureStore(texDst, coord, vec4f(accumColor, 1.0 - transmittance));
}
`;

// ═══════════════════════════════════════════════════════════════════
// 8. VOLUME RAYMARCH — прямой рендеринг медицинского 3D-объёма
// ═══════════════════════════════════════════════════════════════════

export const COMP_VOLUME_RAYMARCH = /* wgsl */`
struct Params {
  uInvViewProj: mat4x4f,
  uCameraPos: vec4f,
  uBBoxMin: vec4f,
  uBBoxMax: vec4f,
  uLightDir: vec4f,
  uResolution: vec2u,
  uSteps: i32,
  uShadowSteps: i32,
  uDensityScale: f32,
  uWindowCenter: f32,   // окно Хаунсфилда — центр
  uWindowWidth: f32,    // окно Хаунсфилда — ширина
  uStepJitter: f32,
  uAmbient: f32,
  uGradientDelta: f32,
  uFrame: f32,
  uPad0: f32,
};

@group(0) @binding(0) var<uniform> U: Params;
@group(0) @binding(1) var texVolume: texture_3d<f32>;
@group(0) @binding(2) var sampVolume: sampler;
@group(0) @binding(3) var texTransfer: texture_2d<f32>;
@group(0) @binding(4) var texDst: texture_storage_2d<rgba16float, write>;

${COMPUTE_COMMON}

fn sampleVolume(p: vec3f) -> f32 {
  let raw = textureSampleLevel(texVolume, sampVolume, p, 0.0).r;
  // Применение окна визуализации — стандарт медицинской визуализации:
  // из всего диапазона плотностей показываем интересующий срез
  let lo = U.uWindowCenter - U.uWindowWidth * 0.5;
  return clamp((raw - lo) / max(U.uWindowWidth, 1e-5), 0.0, 1.0);
}

// Градиент плотности — нормаль к изоповерхности, для затенения
fn volumeGradient(p: vec3f) -> vec3f {
  let d = U.uGradientDelta;
  let gx = sampleVolume(p + vec3f(d, 0.0, 0.0)) - sampleVolume(p - vec3f(d, 0.0, 0.0));
  let gy = sampleVolume(p + vec3f(0.0, d, 0.0)) - sampleVolume(p - vec3f(0.0, d, 0.0));
  let gz = sampleVolume(p + vec3f(0.0, 0.0, d)) - sampleVolume(p - vec3f(0.0, 0.0, d));
  let g = vec3f(gx, gy, gz);
  let len = length(g);
  return select(vec3f(0.0, 0.0, 1.0), g / len, len > 1e-5);
}

fn intersectBox(origin: vec3f, dir: vec3f, bmin: vec3f, bmax: vec3f) -> vec2f {
  let invDir = 1.0 / dir;
  let t0 = (bmin - origin) * invDir;
  let t1 = (bmax - origin) * invDir;
  let tSmall = min(t0, t1);
  let tBig = max(t0, t1);
  return vec2f(
    max(max(tSmall.x, tSmall.y), tSmall.z),
    min(min(tBig.x, tBig.y), tBig.z)
  );
}

@compute @workgroup_size(8, 8, 1)
fn cs_main(@builtin(global_invocation_id) gid: vec3u) {
  let dims = vec2i(U.uResolution);
  let coord = vec2i(gid.xy);
  if (coord.x >= dims.x || coord.y >= dims.y) { return; }

  let uv = (vec2f(coord) + vec2f(0.5)) / vec2f(dims);
  let ndc = vec4f(uv.x * 2.0 - 1.0, (1.0 - uv.y) * 2.0 - 1.0, 1.0, 1.0);
  let world = U.uInvViewProj * ndc;
  let farPoint = world.xyz / max(world.w, 1e-6);

  let origin = U.uCameraPos.xyz;
  let dir = normalize(farPoint - origin);
  let bmin = U.uBBoxMin.xyz;
  let bmax = U.uBBoxMax.xyz;

  let hit = intersectBox(origin, dir, bmin, bmax);
  if (hit.y <= max(hit.x, 0.0)) {
    textureStore(texDst, coord, vec4f(0.0));
    return;
  }

  let tStart = max(hit.x, 0.0);
  let steps = clamp(U.uSteps, 16, 512);
  let dt = (hit.y - tStart) / f32(steps);
  let jitter = hash21(vec2f(coord) + vec2f(U.uFrame)) * U.uStepJitter;

  var accumColor = vec3f(0.0);
  var transmittance = 1.0;
  let lightDir = normalize(U.uLightDir.xyz);

  for (var s = 0; s < 512; s++) {
    if (s >= steps) { break; }
    if (transmittance < 0.005) { break; }

    let t = tStart + (f32(s) + jitter) * dt;
    let pos = origin + dir * t;
    let normPos = (pos - bmin) / max(bmax - bmin, vec3f(1e-6));

    let density = sampleVolume(normPos);
    if (density < 0.01) { continue; }

    // Передаточная функция: плотность → цвет и непрозрачность.
    // Загружается извне, что позволяет менять пресеты тканей без пересборки
    let transfer = textureSampleLevel(texTransfer, sampVolume, vec2f(density, 0.5), 0.0);
    let sigma = transfer.a * density * U.uDensityScale;
    if (sigma < 1e-4) { continue; }

    // Затенение по градиенту + теневой луч к источнику
    let normal = volumeGradient(normPos);
    let diffuse = max(dot(normal, lightDir), 0.0);

    var shadow = 1.0;
    let shadowSteps = clamp(U.uShadowSteps, 0, 32);
    if (shadowSteps > 0) {
      let shadowDt = dt * 2.0;
      var shadowAccum = 0.0;
      for (var ss = 1; ss <= 32; ss++) {
        if (ss > shadowSteps) { break; }
        let sp = normPos + lightDir * (f32(ss) * shadowDt);
        if (any(sp < vec3f(0.0)) || any(sp > vec3f(1.0))) { break; }
        shadowAccum += sampleVolume(sp) * U.uDensityScale * shadowDt;
      }
      shadow = exp(-shadowAccum);
    }

    let lit = transfer.rgb * (U.uAmbient + diffuse * shadow * (1.0 - U.uAmbient));
    let alpha = 1.0 - exp(-sigma * dt);

    accumColor += transmittance * alpha * lit;
    transmittance *= (1.0 - alpha);
  }

  textureStore(texDst, coord, vec4f(accumColor, 1.0 - transmittance));
}
`;

// ═══════════════════════════════════════════════════════════════════
// 9. ДЕКЛАРАТИВНОЕ ОПИСАНИЕ COMPUTE-ПРОХОДОВ
// ═══════════════════════════════════════════════════════════════════

export const COMPUTE_PASSES = Object.freeze({
  denoise: {
    label: 'denoise',
    source: COMP_DENOISE,
    entryPoint: 'cs_main',
    workgroupSize: [8, 8, 1],
    bindings: [
      { binding: 0, kind: 'uniform' },
      { binding: 1, kind: 'texture', name: 'texSrc' },
      { binding: 2, kind: 'texture', name: 'texDepth' },
      { binding: 3, kind: 'storageTexture', name: 'texDst', format: 'rgba16float' },
    ],
    uniforms: [
      ['uResolution', 'vec2u'],
      ['uDepthSigma', 'f32'],
      ['uColorSigma', 'f32'],
      ['uSpatialSigma', 'f32'],
      ['uRadius', 'i32'],
      ['uHasDepth', 'i32'],
      ['uAnisotropic', 'i32'],
      ['uStrength', 'f32'],
      ['uFrame', 'f32'],
    ],
  },

  fsrEasu: {
    label: 'fsrEasu',
    source: COMP_FSR_EASU,
    entryPoint: 'cs_main',
    workgroupSize: [8, 8, 1],
    bindings: [
      { binding: 0, kind: 'uniform' },
      { binding: 1, kind: 'texture', name: 'texSrc' },
      { binding: 2, kind: 'storageTexture', name: 'texDst', format: 'rgba16float' },
    ],
    uniforms: [
      ['uInputSize', 'vec2f'],
      ['uOutputSize', 'vec2f'],
      ['uSharpness', 'f32'],
      ['uPad0', 'f32'],
    ],
  },

  fsrRcas: {
    label: 'fsrRcas',
    source: COMP_FSR_RCAS,
    entryPoint: 'cs_main',
    workgroupSize: [8, 8, 1],
    bindings: [
      { binding: 0, kind: 'uniform' },
      { binding: 1, kind: 'texture', name: 'texSrc' },
      { binding: 2, kind: 'storageTexture', name: 'texDst', format: 'rgba16float' },
    ],
    uniforms: [
      ['uResolution', 'vec2u'],
      ['uSharpness', 'f32'],
      ['uDenoise', 'i32'],
    ],
  },

  sceneStats: {
    label: 'sceneStats',
    source: COMP_SCENE_STATS,
    entryPoint: 'cs_main',
    workgroupSize: [8, 8, 1],
    bindings: [
      { binding: 0, kind: 'uniform' },
      { binding: 1, kind: 'texture', name: 'texSrc' },
      { binding: 2, kind: 'texture', name: 'texDepth' },
      { binding: 3, kind: 'storageBuffer', name: 'stats', access: 'read-write' },
    ],
    uniforms: [
      ['uResolution', 'vec2u'],
      ['uSampleStride', 'i32'],
      ['uHasDepth', 'i32'],
    ],
    // 10 скалярных счётчиков + 16 корзин гистограммы, по 4 байта
    statsBufferSize: (10 + 16) * 4,
  },

  bilateralUpsample: {
    label: 'bilateralUpsample',
    source: COMP_BILATERAL_UPSAMPLE,
    entryPoint: 'cs_main',
    workgroupSize: [8, 8, 1],
    bindings: [
      { binding: 0, kind: 'uniform' },
      { binding: 1, kind: 'texture', name: 'texLow' },
      { binding: 2, kind: 'texture', name: 'texDepthLow' },
      { binding: 3, kind: 'texture', name: 'texDepthFull' },
      { binding: 4, kind: 'storageTexture', name: 'texDst', format: 'rgba16float' },
    ],
    uniforms: [
      ['uLowResSize', 'vec2f'],
      ['uFullResSize', 'vec2f'],
      ['uDepthSigma', 'f32'],
      ['uHasDepth', 'i32'],
    ],
  },

  nerfRender: {
    label: 'nerfRender',
    source: COMP_NERF_RENDER,
    entryPoint: 'cs_main',
    workgroupSize: [8, 8, 1],
    bindings: [
      { binding: 0, kind: 'uniform' },
      { binding: 1, kind: 'storageBuffer', name: 'hashTable', access: 'read-only' },
      { binding: 2, kind: 'storageBuffer', name: 'mlpWeights', access: 'read-only' },
      { binding: 3, kind: 'storageTexture', name: 'texDst', format: 'rgba16float' },
    ],
    uniforms: [
      ['uInvViewProj', 'mat4x4f'],
      ['uCameraPos', 'vec4f'],
      ['uBBoxMin', 'vec4f'],
      ['uBBoxMax', 'vec4f'],
      ['uResolution', 'vec2u'],
      ['uSteps', 'i32'],
      ['uLevels', 'i32'],
      ['uFeaturesPerLevel', 'i32'],
      ['uHashTableSize', 'u32'],
      ['uBaseResolution', 'f32'],
      ['uPerLevelScale', 'f32'],
      ['uDensityScale', 'f32'],
      ['uStepJitter', 'f32'],
      ['uHiddenDim', 'i32'],
      ['uFrame', 'f32'],
    ],
  },

  volumeRaymarch: {
    label: 'volumeRaymarch',
    source: COMP_VOLUME_RAYMARCH,
    entryPoint: 'cs_main',
    workgroupSize: [8, 8, 1],
    bindings: [
      { binding: 0, kind: 'uniform' },
      { binding: 1, kind: 'texture3d', name: 'texVolume' },
      { binding: 2, kind: 'sampler', name: 'sampVolume' },
      { binding: 3, kind: 'texture', name: 'texTransfer' },
      { binding: 4, kind: 'storageTexture', name: 'texDst', format: 'rgba16float' },
    ],
    uniforms: [
      ['uInvViewProj', 'mat4x4f'],
      ['uCameraPos', 'vec4f'],
      ['uBBoxMin', 'vec4f'],
      ['uBBoxMax', 'vec4f'],
      ['uLightDir', 'vec4f'],
      ['uResolution', 'vec2u'],
      ['uSteps', 'i32'],
      ['uShadowSteps', 'i32'],
      ['uDensityScale', 'f32'],
      ['uWindowCenter', 'f32'],
      ['uWindowWidth', 'f32'],
      ['uStepJitter', 'f32'],
      ['uAmbient', 'f32'],
      ['uGradientDelta', 'f32'],
      ['uFrame', 'f32'],
      ['uPad0', 'f32'],
    ],
  },
});

export const COMPUTE_PASS_NAMES = Object.freeze(Object.keys(COMPUTE_PASSES));

/** Разбор буфера статистики сцены (см. структуру Stats в COMP_SCENE_STATS). */
export function parseSceneStats(uint32Array) {
  const FIXED = 1000000;
  const count = Math.max(uint32Array[9], 1);

  const mean = uint32Array[0] / FIXED / count;
  const meanSq = uint32Array[1] / FIXED / count;
  const variance = Math.max(meanSq - mean * mean, 0);

  const histogram = [];
  let histTotal = 0;
  for (let i = 0; i < 16; i++) {
    histogram.push(uint32Array[10 + i]);
    histTotal += uint32Array[10 + i];
  }
  const histNorm = histogram.map(v => (histTotal > 0 ? v / histTotal : 0));

  return {
    brightness: mean,
    contrast: Math.sqrt(variance) * 2,
    variance,
    complexity: uint32Array[2] / FIXED / count,
    minLuma: uint32Array[3] === 0xFFFFFFFF ? 0 : uint32Array[3] / FIXED,
    maxLuma: uint32Array[4] / FIXED,
    darkRatio: uint32Array[5] / count,
    brightRatio: uint32Array[6] / count,
    edgeRatio: uint32Array[7] / count,
    meanDepth: uint32Array[8] / FIXED / count,
    sampleCount: count,
    histogram: histNorm,
  };
}
